import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import useRequireAuth from '../../hooks/useRequireAuth';
import { fetchDocumentTypes } from '../../services/api/DocumentTypeService';
import { getInsuranceTypeName } from '../../utils/insuranceTypeUtils';
import { getOrderedDocumentTypes, normalizeDocumentTypes } from '../../utils/documentTypeUtils';

const AgencyInfoTab = ({ agencyData, onEdit, onReturn }) => {
  const userInfo = useRequireAuth();
  const [documentTypes, setDocumentTypes] = useState([]);

  // 通常どおり日付をフォーマット
  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return format(parseISO(dateString), 'yyyy/MM/dd');
    } catch {
      return '';
    }
  };

  useEffect(() => {
    const fetchDocTypes = async () => {
      if (!userInfo?.is_admin) {
        return;
      }
      try {
        const response = await fetchDocumentTypes();
        setDocumentTypes(normalizeDocumentTypes(response));
      } catch (error) {
        // エラーダイアログ表示はBaseApiServiceで行う
        // エラーコンソール出力は各ApiServiceで行う
      }
    };

    fetchDocTypes();
  }, [userInfo?.is_admin]);

  const renderDocumentTypeList = () => {
    if (documentTypes.length === 0) {
      return '読み込み中...';
    }

    // 代理店に許可された文書種別アクセス権をキー化
    const allowedTypeKeys = new Set(
      (agencyData.document_types || [])
        .map((entry) => {
          const insurance = entry?.insurance_type ?? entry?.insuranceType ?? null;
          const code = entry?.document_type_code ?? entry?.code ?? entry?.document_type ?? null;
          if (!insurance || !code) {
            return null;
          }
          return `${insurance}:${code}`;
        })
        .filter(Boolean)
    );

    const displayParts = [];
    const orderedLifeTypes = getOrderedDocumentTypes(documentTypes, 1);
    const orderedNonLifeTypes = getOrderedDocumentTypes(documentTypes, 2);

    orderedLifeTypes.forEach((type) => {
      if (allowedTypeKeys.has(`${type.insurance_type ?? type.insuranceType}:${type.code}`)) {
        displayParts.push(`${type.name || type.code}\uff08${getInsuranceTypeName(1)}\uff09`);
      }
    });

    orderedNonLifeTypes.forEach((type) => {
      if (allowedTypeKeys.has(`${type.insurance_type ?? type.insuranceType}:${type.code}`)) {
        displayParts.push(`${type.name || type.code}\uff08${getInsuranceTypeName(2)}\uff09`);
      }
    });

    if (displayParts.length === 0) {
      return 'なし';
    }

    return displayParts.join('、');
  };

  return (
    <>
      <table className="info-table c-table-view c-max-middle">
        <tbody>
          <tr>
            {!userInfo.is_agent || userInfo.is_admin && (
              <>
                <th>取引先コード</th>
                <td>{agencyData.trading_code}</td>
              </>
            )}

            <th>アカウント名（メールアドレス）</th>
            <td>{agencyData.accountname}</td>
          </tr>

          <tr>
            <th>代理店名</th>
            <td colSpan="3">{agencyData.name}</td>
          </tr>
          <tr>
            <th>代理店カナ</th>
            <td colSpan="3">{agencyData.name_kana}</td>
          </tr>

          {!userInfo.is_agent || userInfo.is_admin && (
            <tr>
              <th>契約日</th>
              <td>{formatDate(agencyData.contract_date)}</td>
              <th>解除日/契約終了日</th>
              <td>{formatDate(agencyData.cancellation_date)}</td>
            </tr>
          )}

          <tr>
            <th>郵便番号</th>
            <td>{agencyData.postal_code}</td>
            <th>住所・都道府県</th>
            <td>{agencyData.prefecture}</td>
          </tr>
          <tr>
            <th>住所・市区町村</th>
            <td colSpan="3">{agencyData.city}</td>
          </tr>
          <tr>
            <th>住所・ビル名</th>
            <td colSpan="3">{agencyData.building}</td>
          </tr>
          <tr>
            <th>電話番号</th>
            <td colSpan="3">{agencyData.phone}</td>
          </tr>
          {userInfo?.is_admin && (
            <tr>
              <th>文書種別</th>
              <td colSpan="3">
                {renderDocumentTypeList()}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="action-buttons c-max-mini c-m-center">
        <button
          type="button"
          className="button button-secondary button-medium"
          onClick={onReturn}
        >
          戻る
        </button>
        <button
          className="btn btn-primary button-medium"
          onClick={onEdit}
        >
          編集する
        </button>
      </div>
    </>
  );
};

export default AgencyInfoTab;
